import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, RefreshCw, Building2, Package, TrendingUp, TrendingDown,
  ShoppingCart, X, ChevronDown, ChevronUp, Layers, BarChart3,
  ArrowUpRight, ArrowDownRight, Wallet, Clock, Filter, Search
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ax = (url) => axios.get(`${BACKEND_URL}${url}`, { withCredentials: true });

const BROKER_ICONS = {
  interactive_brokers: '🏦', tzero: '🔷', securitize: '🔐',
  alpaca: '🦙', nasdaq: '📈', cme: '🌐',
};

const TYPE_COLORS = {
  etf: 'text-emerald-400 bg-emerald-500/10',
  index: 'text-cyan-400 bg-cyan-500/10',
  securitization: 'text-amber-400 bg-amber-500/10',
  iio: 'text-purple-400 bg-purple-500/10',
  derivative: 'text-red-400 bg-red-500/10',
  asset: 'text-blue-400 bg-blue-500/10',
  portfolio: 'text-pink-400 bg-pink-500/10',
};

/* ── Order Form ────────────────────────────────── */
function OrderForm({ product, brokers, onClose, onSubmit }) {
  const [side, setSide] = useState('buy');
  const [orderType, setOrderType] = useState('market');
  const [quantity, setQuantity] = useState('1');
  const [price, setPrice] = useState(product?.price?.toString() || '');
  const [brokerId, setBrokerId] = useState('interactive_brokers');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const payload = {
        product_id: product.id || product.product_id,
        broker_id: brokerId,
        side,
        quantity: Number(quantity),
        order_type: orderType,
      };
      if (orderType === 'limit' && price) payload.price = Number(price);
      const { data } = await axios.post(`${BACKEND_URL}/api/intangi/order`, payload, { withCredentials: true });
      setResult(data);
      if (onSubmit) onSubmit(data);
    } catch (err) {
      setResult({ error: err.response?.data?.detail || 'Order failed' });
    }
    setLoading(false);
  };

  const total = Number(quantity) * (orderType === 'limit' ? Number(price) : (product?.price || 0));

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="order-modal">
      <div className="bg-[#090B0F] border border-white/10 w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
          <div className="flex items-center gap-2">
            <ShoppingCart className="w-4 h-4 text-emerald-500" />
            <span className="text-sm font-bold text-white">{t('intangi.placeOrder')}</span>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-white"><X className="w-4 h-4" /></button>
        </div>

        {result ? (
          <div className="p-4 space-y-3">
            {result.error ? (
              <div className="bg-red-500/10 border border-red-500/30 p-3 text-sm text-red-400">{result.error}</div>
            ) : (
              <div className="space-y-3">
                <div className="bg-emerald-500/10 border border-emerald-500/30 p-3 text-sm text-emerald-400">
                  Order placed successfully
                </div>
                <div className="space-y-1 text-xs font-mono text-slate-400">
                  <div>{t('intangi.orderId')} <span className="text-white">{result.order_id}</span></div>
                  <div>Status: <span className="text-emerald-400">{result.status}</span></div>
                  {result.executed_price && <div>Price: <span className="text-white">${result.executed_price?.toFixed(2)}</span></div>}
                </div>
              </div>
            )}
            <button onClick={onClose} className="w-full py-2 bg-white/10 text-white text-xs font-mono hover:bg-white/20 transition-colors">{t('common.close')}</button>
          </div>
        ) : (
          <div className="p-4 space-y-3">
            {/* Product */}
            <div className="bg-[#11141D] p-3 flex items-center justify-between">
              <div>
                <div className="text-xs font-bold text-white">{product?.name}</div>
                <div className="text-[10px] text-slate-500 font-mono">{product?.id} | {product?.product_type}</div>
              </div>
              <div className="text-right">
                <div className="text-sm font-bold text-white">${product?.price?.toFixed(2)}</div>
                <div className={`text-[10px] font-mono ${(product?.change_pct || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                  {(product?.change_pct || 0) >= 0 ? '+' : ''}{product?.change_pct?.toFixed(2)}%
                </div>
              </div>
            </div>

            {/* Side */}
            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => setSide('buy')}
                className={`py-2 text-xs font-bold transition-colors ${side === 'buy' ? 'bg-emerald-500 text-black' : 'bg-white/5 text-slate-400 hover:bg-white/10'}`}
                data-testid="order-buy-btn">{t('common.buy')}</button>
              <button onClick={() => setSide('sell')}
                className={`py-2 text-xs font-bold transition-colors ${side === 'sell' ? 'bg-red-500 text-white' : 'bg-white/5 text-slate-400 hover:bg-white/10'}`}
                data-testid="order-sell-btn">{t('common.sell')}</button>
            </div>

            {/* Order type */}
            <div className="grid grid-cols-2 gap-2">
              {['market', 'limit'].map(ot => (
                <button key={ot} onClick={() => setOrderType(ot)}
                  className={`py-1.5 text-[10px] font-mono uppercase transition-colors ${orderType === ot ? 'bg-white/10 text-white' : 'bg-white/5 text-slate-500'}`}>
                  {ot}
                </button>
              ))}
            </div>

            {/* Broker */}
            <div>
              <label className="text-[10px] text-slate-500 uppercase tracking-wider">{t('intangi.broker')}</label>
              <select value={brokerId} onChange={(e) => setBrokerId(e.target.value)}
                className="w-full mt-1 bg-[#11141D] border border-white/10 text-white px-3 py-2 text-xs font-mono focus:border-white/25 outline-none"
                data-testid="order-broker-select">
                {brokers?.map(b => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
            </div>

            {/* Quantity */}
            <div>
              <label className="text-[10px] text-slate-500 uppercase tracking-wider">{t('intangi.quantity')}</label>
              <input type="number" value={quantity} onChange={(e) => setQuantity(e.target.value)} min="0.01" step="0.01"
                className="w-full mt-1 bg-[#11141D] border border-white/10 text-white px-3 py-2 text-xs font-mono focus:border-white/25 outline-none"
                data-testid="order-quantity-input" />
            </div>

            {/* Price (limit only) */}
            {orderType === 'limit' && (
              <div>
                <label className="text-[10px] text-slate-500 uppercase tracking-wider">{t('intangi.price')}</label>
                <input type="number" value={price} onChange={(e) => setPrice(e.target.value)} min="0" step="0.01"
                  className="w-full mt-1 bg-[#11141D] border border-white/10 text-white px-3 py-2 text-xs font-mono focus:border-white/25 outline-none"
                  data-testid="order-price-input" />
              </div>
            )}

            {/* Total */}
            <div className="bg-[#11141D] p-3 flex justify-between">
              <span className="text-[10px] text-slate-500 uppercase">{t('intangi.estTotal')}</span>
              <span className="text-sm font-bold text-white">${total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
            </div>

            <button onClick={handleSubmit} disabled={loading || !quantity}
              className={`w-full py-3 font-bold text-sm transition-colors ${side === 'buy' ? 'bg-emerald-500 text-black hover:bg-emerald-400' : 'bg-red-500 text-white hover:bg-red-400'} disabled:opacity-50`}
              data-testid="order-submit-btn">
              {loading ? <RefreshCw className="w-4 h-4 animate-spin mx-auto" /> : `${side.toUpperCase()} ${product?.id || ''}`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Main Page ────────────────────────────────── */
export default function IntangiTrading() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [brokers, setBrokers] = useState([]);
  const [positions, setPositions] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productDetail, setProductDetail] = useState(null);
  const [showOrder, setShowOrder] = useState(false);
  const [filterType, setFilterType] = useState('all');
  const [filterBroker, setFilterBroker] = useState('all');
  const [searchQ, setSearchQ] = useState('');
  const [tab, setTab] = useState('products');

  const fetchAll = useCallback(async () => {
    try {
      const [prodRes, brokerRes, posRes, ordRes] = await Promise.all([
        ax('/api/intangi/products'),
        ax('/api/intangi/brokers'),
        ax('/api/intangi/positions').catch(() => ({ data: { positions: [] } })),
        ax('/api/intangi/orders?limit=50').catch(() => ({ data: { orders: [] } })),
      ]);
      setProducts(prodRes.data?.products || []);
      setBrokers(brokerRes.data?.brokers || []);
      setPositions(posRes.data?.positions || []);
      setOrders(ordRes.data?.orders || []);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const selectProduct = async (p) => {
    setSelectedProduct(p);
    try {
      const { data } = await ax(`/api/intangi/products/${p.id}`);
      setProductDetail(data);
    } catch {
      setProductDetail(null);
    }
  };

  const filteredProducts = products.filter(p => {
    if (filterType !== 'all' && p.product_type !== filterType) return false;
    if (filterBroker !== 'all' && !(p.available_brokers || []).some(b => b.broker_id === filterBroker)) return false;
    if (searchQ && !p.name?.toLowerCase().includes(searchQ.toLowerCase()) && !p.id?.toLowerCase().includes(searchQ.toLowerCase())) return false;
    return true;
  });

  const productTypes = [...new Set(products.map(p => p.product_type).filter(Boolean))];
  const totalPositionValue = positions.reduce((s, p) => s + (p.market_value || 0), 0);
  const totalPnl = positions.reduce((s, p) => s + (p.unrealized_pnl || 0), 0);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#030508] flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-purple-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#030508] text-white" data-testid="intangi-trading-page">
      {/* Header */}
      <div className="border-b border-white/5 bg-[#030508]/80 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/dashboard')} className="text-slate-500 hover:text-white transition-colors" data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-2">
              <Layers className="w-5 h-5 text-purple-500" />
              <h1 className="text-sm font-black uppercase tracking-[0.15em]">{t('intangi.title')}</h1>
              <span className="text-[10px] font-mono text-purple-400 bg-purple-500/10 px-2 py-0.5">{t('intangi.bridgeVersion')}</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* Portfolio summary */}
            {positions.length > 0 && (
              <div className="hidden sm:flex items-center gap-4 text-xs font-mono">
                <span className="text-slate-400">Positions: <span className="text-white">{positions.length}</span></span>
                <span className="text-slate-400">Value: <span className="text-white">${totalPositionValue.toLocaleString()}</span></span>
                <span className={totalPnl >= 0 ? 'text-emerald-400' : 'text-red-400'}>
                  P&L: {totalPnl >= 0 ? '+' : ''}${totalPnl.toLocaleString()}
                </span>
              </div>
            )}
            <button onClick={fetchAll} className="text-slate-500 hover:text-white transition-colors">
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="max-w-7xl mx-auto px-4 flex gap-0">
          {[
            { key: 'products', label: 'Products', icon: Package, count: products.length },
            { key: 'positions', label: 'Positions', icon: Wallet, count: positions.length },
            { key: 'orders', label: 'Orders', icon: Clock, count: orders.length },
            { key: 'brokers', label: 'Brokers', icon: Building2, count: brokers.length },
          ].map(tab_item => (
            <button key={tab_item.key} onClick={() => setTab(tab_item.key)}
              className={`flex items-center gap-1.5 px-4 py-2 text-xs font-mono transition-colors border-b-2 ${
                tab === tab_item.key ? 'border-purple-500 text-purple-400' : 'border-transparent text-slate-500 hover:text-white'
              }`}
              data-testid={`tab-${tab_item.key}`}>
              <tab_item.icon className="w-3.5 h-3.5" />
              {tab_item.label}
              <span className={`text-[10px] px-1.5 py-0.5 ${tab === tab_item.key ? 'bg-purple-500/20 text-purple-400' : 'bg-white/5 text-slate-600'}`}>
                {tab_item.count}
              </span>
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">

        {/* ── PRODUCTS TAB ── */}
        {tab === 'products' && (
          <div className="flex gap-4">
            {/* Product List */}
            <div className={`${selectedProduct ? 'w-1/2 lg:w-3/5' : 'w-full'} space-y-3`}>
              {/* Filters */}
              <div className="flex flex-wrap gap-2 items-center">
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
                  <input value={searchQ} onChange={(e) => setSearchQ(e.target.value)} placeholder="Search products..."
                    className="w-full bg-[#090B0F] border border-white/10 text-white pl-8 pr-3 py-2 text-xs font-mono focus:border-white/25 outline-none"
                    data-testid="product-search" />
                </div>
                <select value={filterType} onChange={(e) => setFilterType(e.target.value)}
                  className="bg-[#090B0F] border border-white/10 text-white px-3 py-2 text-xs font-mono focus:border-white/25 outline-none"
                  data-testid="filter-type">
                  <option value="all">{t('intangi.allTypes')}</option>
                  {productTypes.map(pt => <option key={pt} value={pt}>{pt.toUpperCase()}</option>)}
                </select>
                <select value={filterBroker} onChange={(e) => setFilterBroker(e.target.value)}
                  className="bg-[#090B0F] border border-white/10 text-white px-3 py-2 text-xs font-mono focus:border-white/25 outline-none"
                  data-testid="filter-broker">
                  <option value="all">{t('intangi.allBrokers')}</option>
                  {brokers.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </div>

              {/* Product Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {filteredProducts.map(p => {
                  const tc = TYPE_COLORS[p.product_type] || 'text-slate-400 bg-white/5';
                  const isSelected = selectedProduct?.id === p.id;
                  return (
                    <div key={p.id} onClick={() => selectProduct(p)}
                      className={`cursor-pointer bg-[#090B0F] border p-3 transition-all ${isSelected ? 'border-purple-500/50 bg-purple-500/5' : 'border-white/5 hover:border-white/15'}`}
                      data-testid={`product-${p.id}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className={`text-[10px] font-mono px-1.5 py-0.5 ${tc}`}>{p.product_type?.toUpperCase()}</span>
                          <span className="text-xs font-bold text-white">{p.id}</span>
                        </div>
                        <div className={`text-xs font-mono font-bold ${(p.change_pct || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                          {(p.change_pct || 0) >= 0 ? '+' : ''}{p.change_pct?.toFixed(2) || '0.00'}%
                        </div>
                      </div>
                      <div className="text-[11px] text-slate-400 mb-2 truncate">{p.name}</div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-bold text-white font-mono">${p.price?.toFixed(2) || '0.00'}</span>
                        <span className="text-[10px] text-slate-500 font-mono">Vol: {(p.volume_24h || 0).toLocaleString()}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Product Detail Panel */}
            {selectedProduct && (
              <div className="w-1/2 lg:w-2/5 space-y-3" data-testid="product-detail">
                <div className="bg-[#090B0F] border border-purple-500/20">
                  {/* Product header */}
                  <div className="p-4 border-b border-white/5">
                    <div className="flex items-center justify-between mb-1">
                      <h2 className="text-sm font-bold text-white">{selectedProduct.name}</h2>
                      <button onClick={() => { setSelectedProduct(null); setProductDetail(null); }}
                        className="text-slate-500 hover:text-white"><X className="w-4 h-4" /></button>
                    </div>
                    <div className="flex items-center gap-2 mb-3">
                      <span className={`text-[10px] font-mono px-1.5 py-0.5 ${TYPE_COLORS[selectedProduct.product_type] || 'bg-white/5 text-slate-400'}`}>
                        {selectedProduct.product_type?.toUpperCase()}
                      </span>
                      <span className="text-[10px] font-mono text-slate-500">{selectedProduct.id}</span>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-2xl font-black text-white">${selectedProduct.price?.toFixed(2)}</span>
                      <span className={`text-sm font-mono font-bold flex items-center gap-1 ${(selectedProduct.change_pct || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                        {(selectedProduct.change_pct || 0) >= 0 ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
                        {(selectedProduct.change_pct || 0) >= 0 ? '+' : ''}{selectedProduct.change_pct?.toFixed(2)}%
                      </span>
                    </div>
                  </div>

                  {/* Stats */}
                  <div className="grid grid-cols-3 gap-px bg-white/5">
                    {[
                      { label: 'Volume 24h', val: (selectedProduct.volume_24h || 0).toLocaleString() },
                      { label: 'High', val: `$${(productDetail?.high_24h || selectedProduct.high_24h || selectedProduct.price || 0).toFixed(2)}` },
                      { label: 'Low', val: `$${(productDetail?.low_24h || selectedProduct.low_24h || selectedProduct.price || 0).toFixed(2)}` },
                    ].map(s => (
                      <div key={s.label} className="bg-[#090B0F] p-2.5 text-center">
                        <div className="text-[9px] text-slate-500 uppercase tracking-wider">{s.label}</div>
                        <div className="text-xs font-mono text-white mt-0.5">{s.val}</div>
                      </div>
                    ))}
                  </div>

                  {/* Order Book */}
                  {productDetail?.order_book_depth && (
                    <div className="p-3 border-t border-white/5">
                      <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-2">{t('intangi.orderBook')}</div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <div className="text-[9px] text-emerald-500 uppercase mb-1">{t('intangi.bids')}</div>
                          {(productDetail.order_book_depth.bids || []).slice(0, 5).map((b, i) => (
                            <div key={i} className="flex justify-between text-[10px] font-mono py-0.5">
                              <span className="text-emerald-400">${b.price?.toFixed(2)}</span>
                              <span className="text-slate-500">{b.quantity}</span>
                            </div>
                          ))}
                        </div>
                        <div>
                          <div className="text-[9px] text-red-500 uppercase mb-1">{t('intangi.asks')}</div>
                          {(productDetail.order_book_depth.asks || []).slice(0, 5).map((a, i) => (
                            <div key={i} className="flex justify-between text-[10px] font-mono py-0.5">
                              <span className="text-red-400">${a.price?.toFixed(2)}</span>
                              <span className="text-slate-500">{a.quantity}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="text-[10px] font-mono text-slate-500 mt-1 text-center">
                        Spread: ${productDetail.order_book_depth.spread?.toFixed(2)}
                      </div>
                    </div>
                  )}

                  {/* Available Brokers */}
                  {selectedProduct.available_brokers?.length > 0 && (
                    <div className="p-3 border-t border-white/5">
                      <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-2">{t('intangi.availableBrokers')}</div>
                      <div className="flex flex-wrap gap-1.5">
                        {selectedProduct.available_brokers.map(b => (
                          <span key={b.broker_id} className="text-[10px] font-mono px-2 py-0.5 bg-white/5 text-slate-400">
                            {BROKER_ICONS[b.broker_id] || ''} {b.broker_name || b.broker_id}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Trade Button */}
                  <div className="p-3 border-t border-white/5">
                    <button onClick={() => setShowOrder(true)}
                      className="w-full py-2.5 bg-purple-500 text-white font-bold text-xs hover:bg-purple-400 transition-colors flex items-center justify-center gap-2"
                      data-testid="trade-btn">
                      <ShoppingCart className="w-4 h-4" /> TRADE {selectedProduct.id}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── POSITIONS TAB ── */}
        {tab === 'positions' && (
          <div className="space-y-3">
            {positions.length === 0 ? (
              <div className="bg-[#090B0F] border border-white/10 p-8 text-center">
                <Wallet className="w-10 h-10 text-slate-600 mx-auto mb-3" />
                <p className="text-sm text-slate-400 mb-1">{t('intangi.noOpenPositions')}</p>
                <p className="text-xs text-slate-600">{t('intangi.noPositionsDesc')}</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-white/10">
                      {['Product', 'Broker', 'Qty', 'Avg Price', 'Current', 'Market Value', 'P&L', 'Return'].map(h => (
                        <th key={h} className="text-left py-2 px-3 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {positions.map((p, i) => (
                      <tr key={i} className="border-b border-white/5 hover:bg-[#090B0F] transition-colors">
                        <td className="py-2 px-3 text-xs font-mono text-white">{p.product_id}</td>
                        <td className="py-2 px-3 text-xs text-slate-400">{p.broker_id}</td>
                        <td className="py-2 px-3 text-xs font-mono text-white">{p.quantity}</td>
                        <td className="py-2 px-3 text-xs font-mono text-white">${p.avg_price?.toFixed(2)}</td>
                        <td className="py-2 px-3 text-xs font-mono text-white">${p.current_price?.toFixed(2)}</td>
                        <td className="py-2 px-3 text-xs font-mono text-white">${p.market_value?.toLocaleString()}</td>
                        <td className={`py-2 px-3 text-xs font-mono ${(p.unrealized_pnl || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                          {(p.unrealized_pnl || 0) >= 0 ? '+' : ''}${p.unrealized_pnl?.toFixed(2)}
                        </td>
                        <td className={`py-2 px-3 text-xs font-mono ${(p.return_pct || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                          {(p.return_pct || 0) >= 0 ? '+' : ''}{p.return_pct?.toFixed(2)}%
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ── ORDERS TAB ── */}
        {tab === 'orders' && (
          <div className="space-y-3">
            {orders.length === 0 ? (
              <div className="bg-[#090B0F] border border-white/10 p-8 text-center">
                <Clock className="w-10 h-10 text-slate-600 mx-auto mb-3" />
                <p className="text-sm text-slate-400 mb-1">{t('intangi.noOrders')}</p>
                <p className="text-xs text-slate-600">{t('intangi.noOrdersDesc')}</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-white/10">
                      {['Order ID', 'Product', 'Side', 'Type', 'Qty', 'Price', 'Status', 'Time'].map(h => (
                        <th key={h} className="text-left py-2 px-3 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((o, i) => (
                      <tr key={i} className="border-b border-white/5 hover:bg-[#090B0F] transition-colors">
                        <td className="py-2 px-3 text-[10px] font-mono text-slate-400">{o.order_id?.slice(0, 12)}</td>
                        <td className="py-2 px-3 text-xs font-mono text-white">{o.product_id}</td>
                        <td className={`py-2 px-3 text-xs font-bold ${o.side === 'buy' ? 'text-emerald-400' : 'text-red-400'}`}>{o.side?.toUpperCase()}</td>
                        <td className="py-2 px-3 text-xs text-slate-400">{o.order_type}</td>
                        <td className="py-2 px-3 text-xs font-mono text-white">{o.quantity}</td>
                        <td className="py-2 px-3 text-xs font-mono text-white">${o.executed_price?.toFixed(2) || o.price?.toFixed(2) || '-'}</td>
                        <td className="py-2 px-3">
                          <span className={`text-[10px] font-mono px-1.5 py-0.5 ${
                            o.status === 'filled' ? 'bg-emerald-500/10 text-emerald-400' :
                            o.status === 'pending' ? 'bg-amber-500/10 text-amber-400' :
                            'bg-white/5 text-slate-400'}`}>{o.status}</span>
                        </td>
                        <td className="py-2 px-3 text-[10px] text-slate-500 font-mono">{o.timestamp?.slice(0, 19)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ── BROKERS TAB ── */}
        {tab === 'brokers' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {brokers.map(b => (
              <div key={b.id} className="bg-[#090B0F] border border-white/5 p-4 hover:border-purple-500/20 transition-colors"
                data-testid={`broker-${b.id}`}>
                <div className="flex items-center gap-3 mb-3">
                  <div className="text-2xl">{BROKER_ICONS[b.id] || '🏢'}</div>
                  <div>
                    <h3 className="text-sm font-bold text-white">{b.name}</h3>
                    <div className="flex items-center gap-1.5">
                      <div className={`w-1.5 h-1.5 rounded-full ${b.status === 'active' ? 'bg-emerald-500' : 'bg-amber-500'}`} />
                      <span className="text-[10px] text-slate-500 font-mono">{b.status}</span>
                    </div>
                  </div>
                </div>
                {b.supported_products && (
                  <div className="space-y-1">
                    <div className="text-[10px] text-slate-500 uppercase tracking-wider">{t('intangi.products')}</div>
                    <div className="flex flex-wrap gap-1">
                      {b.supported_products.map(sp => (
                        <span key={sp} className={`text-[10px] font-mono px-1.5 py-0.5 ${TYPE_COLORS[sp] || 'bg-white/5 text-slate-400'}`}>{sp}</span>
                      ))}
                    </div>
                  </div>
                )}
                {b.fee_structure && (
                  <div className="mt-2 pt-2 border-t border-white/5 flex justify-between text-[10px] font-mono">
                    <span className="text-slate-500">{t('intangi.commission')}</span>
                    <span className="text-white">{b.fee_structure.commission}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Order Modal */}
      {showOrder && selectedProduct && (
        <OrderForm
          product={selectedProduct}
          brokers={brokers}
          onClose={() => setShowOrder(false)}
          onSubmit={() => { setShowOrder(false); fetchAll(); }}
        />
      )}
    </div>
  );
}
